// import { useState, useCallback } from 'react'
// import { chatStream } from '../api/client'

// export function useChat(context) {
//     const [messages, setMessages] = useState([])
//     const [loading, setLoading] = useState(false)

//     const addMessage = (msg) =>
//         setMessages(prev => [...prev, msg])

//     const updateLast = (updater) =>
//         setMessages(prev => {
//             const next = [...prev]
//             next[next.length - 1] = updater(next[next.length - 1])
//             return next
//         })

//     // Inject greeting as first assistant message
//     const addGreeting = useCallback((greeting) => {
//         setMessages([{ role: 'assistant', content: greeting }])
//     }, [])

//     const send = useCallback(async (query) => {
//         if (!query.trim() || loading) return

//         // Build history — pair user+assistant turns together
//         // For greeting (assistant-only first message), send it as standalone answer
//         const history = []
//         const filtered = messages.filter(m => m.content && !m.loading)
//         for (let i = 0; i < filtered.length; i++) {
//             const m = filtered[i]
//             if (m.role === 'user') {
//                 const next = filtered[i + 1]
//                 history.push({
//                     query: m.content,
//                     sql: next?.sql || null,
//                     answer: next?.role === 'assistant' ? next.content : ''
//                 })
//                 i++ // skip the assistant message we just paired
//             } else if (m.role === 'assistant' && i === 0) {
//                 // Greeting — no prior user message, send as context
//                 history.push({ query: '', sql: null, answer: m.content })
//             }
//         }

//         addMessage({ role: 'user', content: query })
//         addMessage({ role: 'assistant', content: '', sql: null, loading: true, userQuery: query })

//         setLoading(true)

//         try {
//             const stream = chatStream({
//                 sessionId: context.sessionId,
//                 query,
//                 allowedBus: context.allowedBus,
//                 userContext: context.user,
//                 history: history   // ← send current chat history to BE
//             })

//             let fullText = ''
//             let sql = null

//             for await (const event of stream) {
//                 if (event.type === 'token') {
//                     fullText += event.data
//                     updateLast(msg => ({ ...msg, content: fullText, loading: false }))
//                 } else if (event.type === 'sql') {
//                     sql = event.data
//                     updateLast(msg => ({ ...msg, sql }))
//                 } else if (event.type === 'meta') {
//                     const vdbScore = event.meta?.vdb_score
//                     const chartConfig = event.meta?.chart_config || null
//                     const rowsSample = event.meta?.rows_sample || []
//                     const isLargeResult = event.meta?.is_large_result || false
//                     const totalRows = event.meta?.total_row_count || 0
//                     updateLast(msg => ({ ...msg, vdb_score: vdbScore, chartConfig, rowsSample, isLargeResult, totalRows }))
//                 } else if (event.type === 'meta') {
//                     // If no answer streamed but we have meta, pipeline may have failed
//                     if (!fullText && event.meta?.retry_count >= 3) {
//                         updateLast(msg => ({
//                             ...msg,
//                             content: 'I was unable to generate a working query after 3 attempts. Please try rephrasing your question.',
//                             loading: false,
//                             error: true
//                         }))
//                     }
//                 } else if (event.type === 'error') {
//                     updateLast(msg => ({
//                         ...msg,
//                         content: 'Something went wrong. Please try again.',
//                         loading: false,
//                         error: true
//                     }))
//                 } else if (event.type === 'done' && !fullText) {
//                     // Stream ended with no tokens — show failure
//                     updateLast(msg => ({
//                         ...msg,
//                         content: msg.content || 'No response generated. Please try rephrasing.',
//                         loading: false
//                     }))
//                 }
//             }
//         } catch (err) {
//             updateLast(msg => ({
//                 ...msg,
//                 content: 'Connection error. Please try again.',
//                 loading: false,
//                 error: true
//             }))
//         } finally {
//             setLoading(false)
//         }
//     }, [context, loading, messages])

//     const clear = useCallback(() => setMessages([]), [])

//     // Load a historical session into the chat window
//     const loadHistory = useCallback((msgs, sessionId) => {
//         setMessages(msgs)
//     }, [])

//     return { messages, loading, send, clear, addGreeting, loadHistory }
// }










import { useState, useCallback } from 'react'
import { chatStream } from '../api/client'

export function useChat(context) {
    const [messages, setMessages] = useState([])
    const [loading, setLoading] = useState(false)
    const [sessionId, setSessionId] = useState(context.sessionId)

    const addMessage = (msg) =>
        setMessages(prev => [...prev, msg])

    const updateLast = (updater) =>
        setMessages(prev => {
            const next = [...prev]
            next[next.length - 1] = updater(next[next.length - 1])
            return next
        })

    // Inject greeting as first assistant message
    const addGreeting = useCallback((greeting) => {
        setMessages([{ role: 'assistant', content: greeting }])
    }, [])

    const send = useCallback(async (query) => {
        if (!query.trim() || loading) return

        const history = []
        const filtered = messages.filter(m => m.content && !m.loading)
        for (let i = 0; i < filtered.length; i++) {
            const m = filtered[i]
            if (m.role === 'user') {
                const next = filtered[i + 1]
                history.push({
                    query: m.content,
                    sql: next?.sql || null,
                    answer: next?.role === 'assistant' ? next.content : ''
                })
                i++
            } else if (m.role === 'assistant' && i === 0) {
                history.push({ query: '', sql: null, answer: m.content })
            }
        }

        addMessage({ role: 'user', content: query })
        addMessage({ role: 'assistant', content: '', sql: null, loading: true, userQuery: query })

        setLoading(true)

        try {
            const stream = chatStream({
                sessionId: sessionId,          // ← use local state, not context.sessionId
                query,
                allowedBus: context.allowedBus,
                userContext: context.user,
                history: history
            })

            let fullText = ''
            let sql = null

            for await (const event of stream) {
                if (event.type === 'session') {
                    // Backend confirms/generates the session id actually used —
                    // keep local state in sync in case it ever differs.
                    if (event.data && event.data !== sessionId) {
                        setSessionId(event.data)
                    }
                } else if (event.type === 'token') {
                    fullText += event.data
                    updateLast(msg => ({ ...msg, content: fullText, loading: false }))
                } else if (event.type === 'sql') {
                    sql = event.data
                    updateLast(msg => ({ ...msg, sql }))
                } else if (event.type === 'meta') {
                    const vdbScore = event.meta?.vdb_score
                    const chartConfig = event.meta?.chart_config || null
                    const rowsSample = event.meta?.rows_sample || []
                    const isLargeResult = event.meta?.is_large_result || false
                    const totalRows = event.meta?.total_row_count || 0
                    updateLast(msg => ({ ...msg, vdb_score: vdbScore, chartConfig, rowsSample, isLargeResult, totalRows }))
                    if (!fullText && event.meta?.retry_count >= 3) {
                        updateLast(msg => ({
                            ...msg,
                            content: 'I was unable to generate a working query after 3 attempts. Please try rephrasing your question.',
                            loading: false,
                            error: true
                        }))
                    }
                } else if (event.type === 'error') {
                    updateLast(msg => ({
                        ...msg,
                        content: 'Something went wrong. Please try again.',
                        loading: false,
                        error: true
                    }))
                } else if (event.type === 'done' && !fullText) {
                    updateLast(msg => ({
                        ...msg,
                        content: msg.content || 'No response generated. Please try rephrasing.',
                        loading: false
                    }))
                }
            }
        } catch (err) {
            updateLast(msg => ({
                ...msg,
                content: 'Connection error. Please try again.',
                loading: false,
                error: true
            }))
        } finally {
            setLoading(false)
        }
    }, [context, loading, messages, sessionId])

    const clear = useCallback(() => setMessages([]), [])

    // Start a brand new session — fresh UUID, empty messages.
    // Passing null as sessionId to chatStream would make the backend
    // generate one, but we generate it client-side so we can reference
    // it immediately (e.g. for the sidebar) without waiting on a round trip.
    const newSession = useCallback(() => {
        const freshId = crypto.randomUUID()
        setSessionId(freshId)
        setMessages([])
        return freshId
    }, [])

    // Load a historical session into the chat window
    const loadHistory = useCallback((msgs, historySessionId) => {
        setSessionId(historySessionId)
        setMessages(msgs)
    }, [])

    return { messages, loading, send, clear, addGreeting, loadHistory, sessionId, newSession }
}